export type TeoEventMap = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: (...args: any[]) => void;
};

export class TeoEventTarget<TEvents extends TeoEventMap> extends EventTarget {
  private eventQueue: Array<{
    type: keyof TEvents & string;
    detail: Parameters<TEvents[keyof TEvents]>[0];
  }> = [];
  private isFlushing = false;

  override addEventListener<K extends keyof TEvents>(
    type: K & string,
    listener: TEvents[K],
  ): () => void {
    const listenerWrapper = (event: Event) => listener((event as CustomEvent).detail);
    super.addEventListener(type, listenerWrapper);
    return () => super.removeEventListener(type, listenerWrapper);
  }

  addEventListeners(listeners: Partial<{ [K in keyof TEvents]: TEvents[K] }>) {
    const unsubscribes = Object.entries(listeners).map(([type, listener]) =>
      this.addEventListener(type, listener),
    );
    return () => unsubscribes.forEach((unsub) => unsub());
  }

  override removeEventListener<K extends keyof TEvents>(
    type: K & string,
    listener: TEvents[K],
  ): void {
    super.removeEventListener(type, listener as EventListener);
  }
  // Ensure that the event is a CustomEvent
  override dispatchEvent(event: CustomEvent): boolean {
    return super.dispatchEvent(event);
  }

  triggerEvent<K extends keyof TEvents>(type: K & string, detail: Parameters<TEvents[K]>[0]): void {
    this.eventQueue.push({ type, detail });
    this.scheduleFlush();
  }

  private scheduleFlush() {
    if (!this.isFlushing) {
      this.isFlushing = true;
      Promise.resolve().then(() => this.flushEvents());
    }
  }

  private flushEvents() {
    while (this.eventQueue.length > 0) {
      const { type, detail } = this.eventQueue.shift()!;
      this.dispatchEvent(new CustomEvent(type, { detail }));
    }
    this.isFlushing = false;
  }
}
